import useAnimationFrame from "$root/hooks/useAnimationFrame";
import {
	type CircularProgressBarProps,
	type IconName,
	type MessageSeverity,
	CircularProgressBar,
	ClickableDiv,
	Icon,
	Text,
	Transition,
} from "@mdotm/shadcn/legacy/components";
import {
	renderNodeOrFn,
	toClassName,
	WrapIfStrOrNumOrEmpty,
	type NodeOrFn,
} from "@mdotm/shadcn/legacy/react-extensions";
import { atom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { Set } from "immutable";
import { type SyntheticEvent, createContext, useCallback, useContext, useState, type CSSProperties } from "react";
import { type Id as ToastId, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toast-overrides.css";

const openToastIds$ = atom(Set<string | number>());

toast.onChange((e) => {
	switch (e.status) {
		case "added": {
			writeAtom(openToastIds$, (curr) => curr.add(e.id));
			break;
		}
		case "removed": {
			writeAtom(openToastIds$, (curr) => curr.remove(e.id));
			break;
		}
		case "updated": {
			// ignored
			break;
		}
	}
});

// for testing
// setTimeout(() => {
// 	platformToast({
// 		icon: "Ask-ai",
// 		children: "tets",
// 		severity: "info",
// 		autoClose: false,
// 	});
// 	platformToast({
// 		icon: "Ask-ai",
// 		children: "tets",
// 		severity: "info",
// 		autoClose: false,
// 	});
// }, 1000);
export function ToastMountPoint(): React.JSX.Element {
	const ids = useAtomValue(openToastIds$);
	return (
		<>
			<ToastContainer stacked limit={10} position="top-right" />
			<Transition
				in={ids.size > 1}
				className="transition-opacity"
				enterFromClassName="opacity-0"
				enterToClassName="opacity-100"
				unmountOnExit
			>
				{({ className }) => (
					<div
						style={{
							position: "fixed",
							top: "calc(var(--toastify-toast-top) - 30px)",
							right: "calc(var(--toastify-toast-right) - 4px)",
							zIndex: "var(--toastify-z-index)",
						}}
						className={toClassName(className)}
					>
						<button
							className="flex bg-white items-center justify-center w-full border border-(--palette-N200) shadow-sm rounded-full px-2"
							type="button"
							onClick={dismissAllPlatformToasts}
						>
							<Icon icon="Close" size={14} />
							&nbsp;
							<Text type="Body/S/Bold" className="mt-0.5">
								Clear all notifications
							</Text>
						</button>
					</div>
				)}
			</Transition>
		</>
	);
}

export type PlatformToastParams = {
	icon: IconName;
	severity: MessageSeverity;
	/** @default 5000 */
	autoClose?: number | false;
	children: NodeOrFn<Omit<ToastContentProps, "children">>;
	/** @default true */
	dismissible?: boolean;
	/** @default 'center' */
	alignIcon?: "top" | "center";
	onClose?(): void;
	onClick?(e: SyntheticEvent): void;
};

const appearanceBySeverity: Record<
	MessageSeverity,
	{
		accentColor: string;
		circleColor: string;
		iconColor: string;
		textColor: string;
	}
> = {
	success: {
		accentColor: themeCSSVars.palette_P500,
		circleColor: themeCSSVars.palette_P200,
		iconColor: themeCSSVars.palette_N0,
		textColor: themeCSSVars.palette_N700,
	},
	info: {
		accentColor: themeCSSVars.palette_S500,
		circleColor: themeCSSVars.palette_S200,
		iconColor: themeCSSVars.palette_N0,
		textColor: themeCSSVars.palette_N700,
	},
	warning: {
		accentColor: themeCSSVars.palette_W500,
		circleColor: themeCSSVars.palette_W200,
		iconColor: themeCSSVars.palette_N0,
		textColor: themeCSSVars.palette_N700,
	},
	error: {
		accentColor: themeCSSVars.palette_D500,
		circleColor: themeCSSVars.palette_D200,
		iconColor: themeCSSVars.palette_N0,
		textColor: themeCSSVars.palette_N700,
	},
};

export type ToastContentProps = {
	children: NodeOrFn<{ onClose(): void }>;
	onClick?(e: SyntheticEvent): void;
	onClose?(): void;
	icon?: IconName;
	severity?: MessageSeverity;
	/** @default 5000 */
	autoClose?: number | false;
	/** @default 'center' */
	alignIcon?: "center" | "top";
};

const ToastCtx = createContext<Partial<ToastContentProps>>({});

export function ToastContent(props: ToastContentProps): React.JSX.Element {
	const ctx = useContext(ToastCtx);

	const severity = props.severity ?? ctx.severity ?? "info";
	const icon = props.icon ?? ctx.icon ?? "Icon-full-info";
	const alignIcon = props.alignIcon ?? ctx.alignIcon ?? "center";
	return (
		<ClickableDiv
			onClick={(e) => {
				e.stopPropagation();
				(props.onClick ?? ctx.onClick)?.(e);
				(props.onClose ?? ctx.onClose)?.();
			}}
			style={
				{
					"--platform-toast-accent-color": appearanceBySeverity[severity].accentColor,
					"--platform-toast-icon-color": appearanceBySeverity[severity].iconColor,
					color: appearanceBySeverity[severity].textColor,
				} as CSSProperties
			}
			className={{
				[`w-full border border-[var(--palette-N200)] before:block before:absolute before:inset-y-0 before:left-0 before:border-2 before:border-(--platform-toast-accent-color) flex gap-4 px-4 py-2`]: true,
				"items-start": alignIcon === "top",
				"items-center": alignIcon === "center",
			}}
			data-qualifier="Toast"
		>
			<div
				className={toClassName({
					"w-[32px] h-[32px] min-w-[32px] min-h-[32px] relative": true,
					"mt-1": alignIcon === "top",
				})}
			>
				{typeof (props.autoClose ?? ctx.autoClose) === "number" && (
					<div className="absolute z-0 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
						<CircularProgressBarThatFollowsTheDefaultBar
							accentColor={appearanceBySeverity[severity].circleColor}
							innerDiameter={34}
							outerDiameter={42}
						/>
					</div>
				)}
				<div className="absolute z-10 w-[32px] h-[32px] min-w-[32px] min-h-[32px] bg-(--platform-toast-accent-color) border-white text-(--platform-toast-icon-color) rounded-full">
					<Icon icon={icon} size={20} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
				</div>
			</div>
			<Text type="Body/L/Book" as="div" className="whitespace-pre-line text-left">
				{renderNodeOrFn(props.children, { onClose: () => (props.onClose ?? ctx.onClose)?.() })}
			</Text>
		</ClickableDiv>
	);
}

export function dismissPlatformToast(id: string | number): void {
	toast.dismiss(id);
	writeAtom(openToastIds$, (curr) => curr.remove(id));
}

export function dismissAllPlatformToasts(): void {
	toast.dismiss();
	writeAtom(openToastIds$, Set());
}

export function platformToast({
	icon,
	severity,
	children,
	autoClose = 5000,
	dismissible = true,
	alignIcon,
	onClose,
	onClick,
}: PlatformToastParams): string | number {
	let toastId: ToastId | null = null;
	const props: Omit<ToastContentProps, "children"> = {
		icon,
		severity,
		autoClose,
		alignIcon,
		onClick,
		onClose: () => {
			if (toastId != null) {
				dismissPlatformToast(toastId);
			}
		},
	};
	toastId = toast(
		<ToastCtx.Provider value={props}>
			<WrapIfStrOrNumOrEmpty
				wrapper={(content) => (
					<ToastContent {...props}>
						<Text type="Body/L/Bold">{content}</Text>
					</ToastContent>
				)}
			>
				{renderNodeOrFn(children, props)}
			</WrapIfStrOrNumOrEmpty>
		</ToastCtx.Provider>,
		{
			autoClose,
			hideProgressBar: true,
			type: severity,
			onClose,
			icon: false,
			closeButton: dismissible,
		},
	);

	writeAtom(openToastIds$, (curr) => curr.add(toastId));
	return toastId;
}

export function TodoToast(): void {
	platformToast({
		children: "TODO: Missing Implementation",
		icon: "Portfolio",
		severity: "info",
	});
}

function CircularProgressBarThatFollowsTheDefaultBar({
	...forward
}: Omit<CircularProgressBarProps, "value" | "innerRef">) {
	const [elements, _setElements] = useState<{
		toastEl: HTMLElement;
		progressBarEl: HTMLElement;
	} | null>(null);
	const setElements = useCallback((el: SVGSVGElement | null) => {
		if (!el) {
			return;
		}
		const toastEl = el.closest<HTMLElement>(".Toastify__toast");
		if (!toastEl) {
			return;
		}
		const progressBarEl = toastEl.querySelector<HTMLElement>('[role="progressbar"]');
		if (!progressBarEl) {
			return;
		}
		_setElements({ toastEl, progressBarEl });
	}, []);
	const [value, setValue] = useState(1);
	// It's not the prettiest solution, but I haven't found a way of getting the current progress value from react-toastify. Feel free to improve this code
	useAnimationFrame(
		() => {
			if (!elements) {
				return;
			}
			const totalWidth = elements.toastEl.offsetWidth;
			// When the toast has no measurable width (before layout, or under jsdom where every
			// getBoundingClientRect is 0) the raw ratio would be 0/0 = NaN. A NaN progress value is never
			// correct, and — because `NaN === 0` is false — it also defeats the pause guard below, leaving
			// this requestAnimationFrame loop spinning forever. Coerce a non-measurable width to 0 so the
			// guard can stop it; a real browser always reports a positive width, so this is a no-op there.
			setValue(totalWidth > 0 ? elements.progressBarEl.getBoundingClientRect().width / totalWidth : 0);
		},
		// Pause once the bar has fully drained (value 0) OR could not be measured (value not > 0). Using
		// `!(value > 0)` rather than `value === 0` additionally stops the loop on a non-finite reading.
		{ pause: !elements || !(value > 0) },
	);
	return (
		<CircularProgressBar {...forward} className="[&>circle]:transition-none" innerRef={setElements} value={value} />
	);
}
